"use client";

import { useEffect, useRef } from "react";

type BridgeMessage =
  | { type: "cad-bridge-init" }
  | { type: "cad-bridge-stop" }
  | { type: "cad-keydown"; key: string; code: string; repeat?: boolean; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean; metaKey?: boolean }
  | { type: "cad-keyup"; key: string; code: string; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean; metaKey?: boolean };

declare global {
  interface Window {
    __ROUGE_FIVEM_DUI__?: boolean;
  }
}

function isTextEditable(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  return !!el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
}

function isEditable(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement | HTMLElement {
  if (!el) return false;
  if (isTextEditable(el)) return !el.disabled && !el.readOnly;
  return el instanceof HTMLElement && el.isContentEditable;
}

function isKeyboardTarget(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return !el.disabled;
  }
  if (el.isContentEditable) return true;
  return el.matches('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
}

function nativeValueSetter(el: HTMLInputElement | HTMLTextAreaElement) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  return Object.getOwnPropertyDescriptor(proto, "value")?.set;
}

function getSelection(el: HTMLInputElement | HTMLTextAreaElement, fallback: { start: number; end: number }) {
  // If React replaced the controlled element after an input event, the new
  // element is connected but not focused and its selection is usually 0/0.
  // In that case keep the logical caret we tracked instead of jumping back
  // to the start on every character.
  if (document.activeElement !== el) return fallback;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  if (typeof start === "number" && typeof end === "number") return { start, end };
  return fallback;
}

function dispatchReactInput(el: HTMLInputElement | HTMLTextAreaElement, oldValue: string, inputType: string, data: string | null) {
  const tracker = (el as HTMLInputElement & { _valueTracker?: { setValue: (v: string) => void } })._valueTracker;
  // React's controlled-input tracker must see the old value before the native
  // setter runs, otherwise React can decide that nothing changed.
  tracker?.setValue(oldValue);
  el.dispatchEvent(new InputEvent("input", { bubbles: true, inputType, data }));
}

function setTextWithoutRefocus(
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string,
  selectionStart: number,
  selectionEnd: number,
  inputType: string,
  data: string | null,
) {
  const oldValue = el.value;
  const setter = nativeValueSetter(el);
  if (setter) setter.call(el, value);
  else el.value = value;

  try {
    el.setSelectionRange(selectionStart, selectionEnd);
  } catch {
    // Some input types do not support a selection range.
  }

  dispatchReactInput(el, oldValue, inputType, data);
}

function getElementIdentity(el: HTMLElement) {
  return {
    tag: el.tagName,
    id: el.id,
    name: el.getAttribute("name") ?? "",
    type: el.getAttribute("type") ?? "",
    placeholder: el.getAttribute("placeholder") ?? "",
    aria: el.getAttribute("aria-label") ?? "",
  };
}

function findElementByIdentity(identity: ReturnType<typeof getElementIdentity>) {
  const tag = identity.tag.toLowerCase();
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(tag));
  return candidates.find((el) =>
    (!identity.id || el.id === identity.id) &&
    (!identity.name || el.getAttribute("name") === identity.name) &&
    (!identity.type || el.getAttribute("type") === identity.type) &&
    (!identity.placeholder || el.getAttribute("placeholder") === identity.placeholder) &&
    (!identity.aria || el.getAttribute("aria-label") === identity.aria)
  ) ?? null;
}

function moveCaret(el: HTMLInputElement | HTMLTextAreaElement, key: string, shift: boolean, ctrl: boolean) {
  const len = el.value.length;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? start;
  let pos = start;

  // Ctrl+Left/Right is intentionally simplified to document boundaries. It
  // is much safer across CEF versions than trying to reproduce word-break
  // behavior manually.
  if (key === "ArrowLeft") pos = ctrl ? 0 : Math.max(0, start - 1);
  else if (key === "ArrowRight") pos = ctrl ? len : Math.min(len, end + 1);
  else if (key === "Home") pos = 0;
  else if (key === "End") pos = len;

  try {
    if (shift) el.setSelectionRange(end, pos);
    else el.setSelectionRange(pos, pos);
  } catch {}
}

function deleteText(el: HTMLInputElement | HTMLTextAreaElement, backwards: boolean, fallback: { start: number; end: number }) {
  const { start, end } = getSelection(el, fallback);
  let from = start;
  let to = end;

  if (start === end) {
    if (backwards) {
      if (start === 0) return fallback;
      from = start - 1;
    } else {
      if (end >= el.value.length) return fallback;
      to = end + 1;
    }
  }

  const next = el.value.slice(0, from) + el.value.slice(to);
  setTextWithoutRefocus(el, next, from, from, backwards ? "deleteContentBackward" : "deleteContentForward", null);
  return { start: from, end: from };
}

function insertText(el: HTMLInputElement | HTMLTextAreaElement, text: string, fallback: { start: number; end: number }) {
  const { start, end } = getSelection(el, fallback);
  const next = el.value.slice(0, start) + text + el.value.slice(end);
  const caret = start + text.length;
  setTextWithoutRefocus(el, next, caret, caret, "insertText", text);
  return { start: caret, end: caret };
}

function commitSelectValue(select: HTMLSelectElement, index: number) {
  if (!select.options.length) return;
  const next = Math.max(0, Math.min(select.options.length - 1, index));
  if (next === select.selectedIndex) return;

  const oldValue = select.value;
  const option = select.options[next];
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")?.set;
  const tracker = (select as HTMLSelectElement & { _valueTracker?: { setValue?: (v: string) => void } })._valueTracker;
  tracker?.setValue?.(oldValue);

  if (setter) setter.call(select, option.value);
  else select.value = option.value;

  select.dispatchEvent(new Event("input", { bubbles: true }));
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

function getFocusableElements() {
  return Array.from(document.querySelectorAll<HTMLElement>(
    'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
  )).filter((el) => el.offsetParent !== null && !el.hasAttribute("aria-hidden"));
}

export default function FivemDuiBridge() {
  const enabled = useRef(false);
  const selectMenu = useRef<HTMLDivElement | null>(null);
  const selectedSelect = useRef<HTMLSelectElement | null>(null);
  const lastKeyboardTarget = useRef<HTMLElement | null>(null);
  const lastIdentity = useRef<ReturnType<typeof getElementIdentity> | null>(null);
  const selection = useRef({ start: 0, end: 0 });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    enabled.current = params.get("fivemDui") === "1";
    window.__ROUGE_FIVEM_DUI__ = enabled.current;

    const closeSelectMenu = () => {
      selectMenu.current?.remove();
      selectMenu.current = null;
      selectedSelect.current = null;
    };

    const rememberTarget = (target: Element | null) => {
      if (!(target instanceof HTMLElement)) return;
      if (!isKeyboardTarget(target)) return;
      lastKeyboardTarget.current = target;
      lastIdentity.current = getElementIdentity(target);
      if (isTextEditable(target)) selection.current = getSelection(target, selection.current);
    };

    const openSelectMenu = (select: HTMLSelectElement) => {
      if (!enabled.current || select.disabled) return;
      closeSelectMenu();
      rememberTarget(select);

      const rect = select.getBoundingClientRect();
      const menu = document.createElement("div");
      menu.style.position = "fixed";
      menu.style.zIndex = "2147483647";
      menu.style.left = `${rect.left}px`;
      menu.style.top = `${rect.bottom}px`;
      menu.style.minWidth = `${Math.max(rect.width, 180)}px`;
      menu.style.maxHeight = "320px";
      menu.style.overflowY = "auto";
      menu.style.background = getComputedStyle(select).backgroundColor || "#111827";
      menu.style.border = getComputedStyle(select).border || "1px solid rgba(148,163,184,.5)";
      menu.style.borderRadius = getComputedStyle(select).borderRadius || "4px";
      menu.style.boxShadow = "0 8px 24px rgba(0,0,0,.45)";
      menu.style.color = getComputedStyle(select).color || "#fff";
      menu.style.font = getComputedStyle(select).font;
      menu.style.padding = "3px";
      menu.style.boxSizing = "border-box";

      Array.from(select.options).forEach((option, index) => {
        const item = document.createElement("div");
        item.textContent = option.textContent ?? "";
        item.style.padding = "7px 9px";
        item.style.minHeight = "28px";
        item.style.boxSizing = "border-box";
        item.style.userSelect = "none";
        item.style.cursor = "default";
        item.style.background = index === select.selectedIndex ? "rgba(59,130,246,.35)" : "transparent";
        item.addEventListener("mouseenter", () => { item.style.background = "rgba(59,130,246,.35)"; });
        item.addEventListener("mouseleave", () => {
          if (index !== select.selectedIndex) item.style.background = "transparent";
        });
        item.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation();
          commitSelectValue(select, index);
          closeSelectMenu();
        });
        menu.appendChild(item);
      });

      document.body.appendChild(menu);
      selectMenu.current = menu;
      selectedSelect.current = select;
      menu.scrollTop = Math.max(0, select.selectedIndex) * 34 - 100;
    };

    const onMouseDown = (event: MouseEvent) => {
      if (!enabled.current) return;
      const target = event.target as Element | null;
      const select = target?.closest("select") as HTMLSelectElement | null;
      if (select) {
        event.preventDefault();
        event.stopPropagation();
        openSelectMenu(select);
        return;
      }

      if (selectMenu.current && !selectMenu.current.contains(event.target as Node)) closeSelectMenu();
      rememberTarget(target?.closest('input,textarea,select,button,a,[contenteditable="true"]') ?? target);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!enabled.current) return;
      rememberTarget(event.target as Element | null);
    };

    const onKeySelectionChange = () => {
      const active = document.activeElement;
      if (isTextEditable(active)) selection.current = getSelection(active, selection.current);
    };

    const resolveKeyboardTarget = () => {
      const active = document.activeElement;
      if (isKeyboardTarget(active)) {
        rememberTarget(active);
        return active;
      }

      const remembered = lastKeyboardTarget.current;
      if (remembered && remembered.isConnected && isKeyboardTarget(remembered)) return remembered;

      if (lastIdentity.current) {
        const found = findElementByIdentity(lastIdentity.current);
        if (found && isKeyboardTarget(found)) {
          lastKeyboardTarget.current = found;
          return found;
        }
      }

      return null;
    };

    const onClick = (event: MouseEvent) => {
      if (!enabled.current) return;
      const target = event.target as Element | null;
      if (selectMenu.current && !selectMenu.current.contains(event.target as Node)) {
        if (!target?.closest("select")) closeSelectMenu();
      }
    };

    const onMessage = (event: MessageEvent<BridgeMessage> | MessageEvent<string>) => {
      let data = event.data as BridgeMessage | string;
      if (typeof data === "string") {
        try { data = JSON.parse(data) as BridgeMessage; } catch { return; }
      }
      if (!data || typeof data !== "object" || !("type" in data)) return;

      if (data.type === "cad-bridge-init") {
        enabled.current = true;
        window.__ROUGE_FIVEM_DUI__ = true;
        return;
      }
      if (data.type === "cad-bridge-stop") {
        enabled.current = false;
        window.__ROUGE_FIVEM_DUI__ = false;
        closeSelectMenu();
        lastKeyboardTarget.current = null;
        lastIdentity.current = null;
        selection.current = { start: 0, end: 0 };
        return;
      }
      if (!enabled.current || data.type !== "cad-keydown") return;

      const active = resolveKeyboardTarget();
      if (!active) return;

      const key = data.key;
      const ctrl = !!data.ctrlKey || !!data.metaKey;
      const shift = !!data.shiftKey;

      if (active instanceof HTMLSelectElement) {
        if (key === "ArrowDown" || key === "ArrowRight") { commitSelectValue(active, active.selectedIndex + 1); return; }
        if (key === "ArrowUp" || key === "ArrowLeft") { commitSelectValue(active, active.selectedIndex - 1); return; }
        if (key === "Home") { commitSelectValue(active, 0); return; }
        if (key === "End") { commitSelectValue(active, active.options.length - 1); return; }
        if (key === "Enter" || key === " ") { openSelectMenu(active); return; }
        return;
      }

      if (active instanceof HTMLButtonElement || active instanceof HTMLAnchorElement) {
        if (key === "Enter" || key === " ") active.click();
        return;
      }

      if (!isEditable(active)) return;

      if (isTextEditable(active)) {
        if (ctrl && key.toLowerCase() === "a") {
          active.select();
          selection.current = { start: 0, end: active.value.length };
          return;
        }
        if (key === "Backspace") {
          selection.current = deleteText(active, true, selection.current);
          return;
        }
        if (key === "Delete") {
          selection.current = deleteText(active, false, selection.current);
          return;
        }
        if (key === "ArrowLeft" || key === "ArrowRight" || key === "Home" || key === "End") {
          moveCaret(active, key, shift, ctrl);
          selection.current = getSelection(active, selection.current);
          return;
        }
        if (key === "Tab") {
          const focusable = getFocusableElements();
          const index = focusable.indexOf(active);
          if (index >= 0 && focusable.length) {
            const next = focusable[(index + (shift ? -1 : 1) + focusable.length) % focusable.length];
            next?.focus({ preventScroll: true });
            rememberTarget(next ?? null);
          }
          return;
        }
        if (key === "Enter") {
          if (active instanceof HTMLTextAreaElement) {
            selection.current = insertText(active, "\n", selection.current);
            return;
          }
          const form = active.closest("form") as HTMLFormElement | null;
          if (form) form.requestSubmit?.();
          return;
        }
        if (!ctrl && !data.altKey && key.length === 1) {
          selection.current = insertText(active, key, selection.current);
        }
        return;
      }

      // Basic contenteditable support.
      if (active.isContentEditable && key.length === 1 && !ctrl && !data.altKey) {
        document.execCommand("insertText", false, key);
      }
    };

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("selectionchange", onKeySelectionChange, true);
    document.addEventListener("click", onClick, true);
    window.addEventListener("message", onMessage as EventListener);

    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("selectionchange", onKeySelectionChange, true);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("message", onMessage as EventListener);
      closeSelectMenu();
    };
  }, []);

  return null;
}
